import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { apiPost } from '../api/client';
import { useT } from '../i18n/LangProvider';
import { LoginScreen } from './LoginScreen';
import { BrandMark, Icon } from '../ui/Icon';

interface AuthorizeParams {
  client_id: string;
  redirect_uri: string;
  response_type: string;
  scope?: string;
  state?: string;
  nonce?: string;
  code_challenge: string;
  code_challenge_method: string;
}

interface AuthorizeResult {
  redirectTo?: string;
  consentRequired?: boolean;
  client?: { name: string; clientId: string };
  scope?: string;
}

const SCOPE_LABELS: Record<string, string> = {
  openid: 'Verificare la tua identità',
  profile: 'Il tuo nome',
  email: 'Il tuo indirizzo email',
  roles: 'I tuoi ruoli aziendali',
  offline_access: 'Restare connesso anche quando chiudi la pagina',
};

function readParams(): AuthorizeParams | null {
  const q = new URLSearchParams(window.location.search);
  const get = (k: string) => q.get(k) ?? undefined;
  const client_id = get('client_id');
  const redirect_uri = get('redirect_uri');
  const code_challenge = get('code_challenge');
  if (!client_id || !redirect_uri || !code_challenge) return null;
  return {
    client_id,
    redirect_uri,
    response_type: get('response_type') ?? 'code',
    scope: get('scope'),
    state: get('state'),
    nonce: get('nonce'),
    code_challenge,
    code_challenge_method: get('code_challenge_method') ?? 'S256',
  };
}

/** Send the user back to the relying party — with a code, or with an error. */
function bounce(redirectUri: string, params: Record<string, string | undefined>) {
  const url = new URL(redirectUri);
  for (const [k, v] of Object.entries(params)) if (v) url.searchParams.set(k, v);
  window.location.href = url.toString();
}

export function AuthorizeScreen() {
  const t = useT();
  const auth = useAuth();
  const params = readParams();
  const [error, setError] = useState<string | null>(null);
  const [consent, setConsent] = useState<AuthorizeResult | null>(null);
  const [working, setWorking] = useState(true);

  async function request(approve: boolean) {
    if (!params) return;
    setWorking(true);
    setError(null);
    try {
      const res = await apiPost<AuthorizeResult>('/oauth/authorize', { ...params, approve });
      if (res.redirectTo) {
        window.location.href = res.redirectTo;
        return;
      }
      if (res.consentRequired) {
        setConsent(res);
        setWorking(false);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Richiesta di autorizzazione non valida.'));
      setWorking(false);
    }
  }

  // As soon as the user is authenticated, ask the server: trusted apps redirect
  // immediately (no re-login), others surface a consent screen.
  useEffect(() => {
    if (auth.ready && auth.authenticated && params && !consent) {
      void request(false);
    }
    if (auth.ready && (!params)) setWorking(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.ready, auth.authenticated]);

  if (!params) {
    return (
      <div className="login-shell">
        <div className="card" style={{ padding: 28, maxWidth: 440, margin: 'auto', textAlign: 'center' }}>
          <Icon name="warn" size={28} />
          <h2 className="h2" style={{ margin: '10px 0 6px' }}>{t('Richiesta non valida')}</h2>
          <p className="muted" style={{ fontSize: 13.5 }}>
            {t('Mancano dei parametri obbligatori (client_id, redirect_uri, code_challenge).')}
          </p>
        </div>
      </div>
    );
  }

  // Not logged in → show the normal login; once authenticated the effect resumes.
  if (auth.ready && !auth.authenticated) {
    return <LoginScreen />;
  }

  const deny = () =>
    bounce(params.redirect_uri, { error: 'access_denied', state: params.state });

  const scopes = (consent?.scope ?? params.scope ?? 'openid').split(/\s+/).filter(Boolean);

  return (
    <div className="login-shell">
      <div className="card oauth-consent" style={{ maxWidth: 460, margin: 'auto' }}>
        <div className="oauth-consent__head">
          <BrandMark size={40} />
          <Icon name="arrowR" size={16} />
          <div className="oauth-consent__app">{consent?.client?.name ?? t('Applicazione')}</div>
        </div>

        {working && !consent ? (
          <div className="oauth-consent__body" style={{ textAlign: 'center' }}>
            <div className="muted" style={{ fontSize: 13.5 }}>{t('Autorizzazione in corso…')}</div>
          </div>
        ) : error ? (
          <div className="oauth-consent__body">
            <div className="lrn-quiz__result is-fail" style={{ borderRadius: 8 }}>
              <Icon name="warn" size={15} /> {error}
            </div>
            <button className="btn" style={{ marginTop: 12 }} onClick={deny}>{t('Torna indietro')}</button>
          </div>
        ) : (
          <div className="oauth-consent__body">
            <p style={{ fontSize: 14, margin: '0 0 6px' }}>
              <strong>{consent?.client?.name ?? t('Questa applicazione')}</strong> {t('vuole accedere al tuo account Touristation.')}
            </p>
            <p className="muted" style={{ fontSize: 12.5, margin: '0 0 14px' }}>
              {t('Accederai come')} <strong>{auth.name || auth.email}</strong>.
            </p>
            <div className="oauth-consent__scopes">
              {scopes.map((s) => (
                <div key={s} className="oauth-consent__scope">
                  <Icon name="check" size={13} /> {t(SCOPE_LABELS[s] ?? s)}
                </div>
              ))}
            </div>
            <div className="cluster" style={{ justifyContent: 'flex-end', marginTop: 18, gap: 8 }}>
              <button className="btn" onClick={deny}>{t('Annulla')}</button>
              <button className="btn btn--brand" onClick={() => void request(true)}>{t('Consenti')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
